// frontend/src/components/SecuritySettings.js - Themed version with professional design
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from '../axiosConfig';
import { useChangeTracker } from '../utils/changeTracker';
import { validatePassword } from '../utils/passwordValidator';
import { Lock, Shield, Key, Save, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { ThemedButton, ThemedCard, ThemedInput } from './themed';

const SecuritySettings = () => {
  const { t } = useTranslation();
  const { text, cn } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('password');
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Security questions state
  const [securityQuestions, setSecurityQuestions] = useState({
    question_1: '',
    answer_1: '',
    question_2: '',
    answer_2: '',
    question_3: '',
    answer_3: ''
  });
  const [hasSecurityQuestions, setHasSecurityQuestions] = useState(false);

  // Change tracking for security questions
  const securityQuestionsTracker = useChangeTracker({});

  // Predefined security questions
  const questionOptions = [
    t('securityQuestion1'), // What was the name of your first pet?
    t('securityQuestion2'), // What is your mother's maiden name?
    t('securityQuestion3'), // What was the name of your first school?
    t('securityQuestion4'), // In what city were you born?
    t('securityQuestion5'), // What is your favorite movie?
    t('securityQuestion6'), // What was your childhood nickname?
    t('securityQuestion7'), // What is the name of your favorite teacher?
    t('securityQuestion8'), // What was the make of your first car?
    t('securityQuestion9'), // What is your favorite food?
    t('securityQuestion10') // What year did you graduate high school?
  ];

  useEffect(() => {
    fetchSecurityQuestions();
  }, []);

  const fetchSecurityQuestions = async () => {
    try {
      const response = await axios.get('/api/settings/security-questions');
      if (response.data.success && response.data.data) {
        setHasSecurityQuestions(true);
        const fetchedQuestions = {
          question_1: response.data.data.question_1,
          answer_1: '', // Don't show existing answers
          question_2: response.data.data.question_2,
          answer_2: '',
          question_3: response.data.data.question_3 || '',
          answer_3: ''
        };
        setSecurityQuestions(fetchedQuestions);
        // Update original values for change tracking
        securityQuestionsTracker.updateOriginalValues(fetchedQuestions);
      }
    } catch (error) {
      console.error('Error fetching security questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (field, value) => {
    setPasswordForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSecurityQuestionChange = (field, value) => {
    setSecurityQuestions(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validatePasswordForm = () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error(t('allPasswordFieldsRequired'));
      return false;
    }

    // Validate password complexity
    const passwordValidation = validatePassword(passwordForm.newPassword);
    if (!passwordValidation.isValid) {
      toast.error(passwordValidation.message);
      return false;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error(t('passwordsDoNotMatch'));
      return false;
    }

    if (passwordForm.currentPassword === passwordForm.newPassword) {
      toast.error(t('newPasswordMustBeDifferent'));
      return false;
    }

    return true;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePasswordForm()) return;

    setSaving(true);
    try {
      const response = await axios.put('/api/settings/password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });

      if (response.data.success) {
        toast.success(t('passwordChangedSuccessfully'));
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.error || t('errorChangingPassword'));
    } finally {
      setSaving(false);
    }
  };

  const validateSecurityQuestions = () => {
    if (!securityQuestions.question_1 || !securityQuestions.answer_1 ||
        !securityQuestions.question_2 || !securityQuestions.answer_2) {
      toast.error(t('twoSecurityQuestionsRequired'));
      return false;
    }

    if (securityQuestions.answer_1.length < 3 || securityQuestions.answer_2.length < 3) {
      toast.error(t('answerMinLength'));
      return false;
    }

    if (securityQuestions.question_3 && !securityQuestions.answer_3) {
      toast.error(t('answerRequiredForQuestion3'));
      return false;
    }

    return true;
  };

  const handleSecurityQuestionsSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateSecurityQuestions()) return;

    setSaving(true);
    try {
      // Get only changed fields
      const changedQuestions = securityQuestionsTracker.getChangedFields(securityQuestions);
      
      // Only make API call if there are changes
      if (Object.keys(changedQuestions).length > 0) {
        console.log('Saving changed security questions:', changedQuestions);
        
        // For security questions, we need to send complete pairs if any part changed
        // Build the payload with complete question-answer pairs that have changes
        const questionsToUpdate = {};
        
        // Check each question pair
        for (let i = 1; i <= 3; i++) {
          const questionKey = `question_${i}`;
          const answerKey = `answer_${i}`;
          
          if (changedQuestions[questionKey] || changedQuestions[answerKey]) {
            questionsToUpdate[questionKey] = securityQuestions[questionKey];
            questionsToUpdate[answerKey] = securityQuestions[answerKey];
          }
        }
        
        await axios.put('/api/settings/security-questions', questionsToUpdate);
        
        // Update original values after successful save
        securityQuestionsTracker.updateOriginalValues(securityQuestions);
        
        toast.success(t('securityQuestionsUpdatedSuccessfully'));
        setHasSecurityQuestions(true);
      } else {
        toast.info(t('noChangesToSave'));
      }
    } catch (error) {
      toast.error(error.response?.data?.error || t('errorUpdatingSecurityQuestions'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className={cn("text-2xl font-bold", text.primary)}>{t('security')}</h2>
        <p className={cn("mt-1 text-sm", text.muted)}>{t('manageYourSecuritySettings')}</p>
      </div>

      {/* Tabs */}
      <div className={cn("border-b", "border-gray-200 dark:border-gray-700")}>
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('password')}
            className={cn(
              "py-2 px-1 border-b-2 font-medium text-sm transition-colors",
              activeTab === 'password'
                ? "border-blue-500 text-blue-600 dark:text-blue-400"
                : cn("border-transparent", text.muted, "hover:border-gray-300 dark:hover:border-gray-600", "hover:text-gray-700 dark:hover:text-gray-300")
            )}
          >
            {t('changePassword')}
          </button>
          <button
            onClick={() => setActiveTab('questions')}
            className={cn(
              "py-2 px-1 border-b-2 font-medium text-sm transition-colors",
              activeTab === 'questions'
                ? "border-blue-500 text-blue-600 dark:text-blue-400"
                : cn("border-transparent", text.muted, "hover:border-gray-300 dark:hover:border-gray-600", "hover:text-gray-700 dark:hover:text-gray-300")
            )}
          >
            {t('securityQuestions')}
          </button>
        </nav>
      </div>

      {/* Change Password Tab */}
      {activeTab === 'password' && (
        <ThemedCard>
          <ThemedCard.Header>
            <Lock className="inline w-5 h-5 mr-2" />
            {t('changeYourPassword')}
          </ThemedCard.Header>
          <form onSubmit={handlePasswordSubmit}>
            <ThemedCard.Body className="space-y-4">
              
                <ThemedInput
                  label={t('currentPassword')}
                  type={showPasswords.current ? 'text' : 'password'}
                  value={passwordForm.currentPassword}
                  onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                  required
                  righticon={
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                      className={cn("text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors")}
                    >
                      {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  }
                />

                <ThemedInput
                  label={t('newPassword')}
                  type={showPasswords.new ? 'text' : 'password'}
                  value={passwordForm.newPassword}
                  onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                  required
                  helperText={t('passwordRequirements')}
                  righticon={
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                      className={cn("text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors")}
                    >
                      {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  }
                />

                <ThemedInput
                  label={t('confirmNewPassword')}
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={passwordForm.confirmPassword}
                  onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                  required
                  righticon={
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                      className={cn("text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors")}
                    >
                      {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  }
                />
            </ThemedCard.Body>
            <ThemedCard.Footer>
              <div className="flex justify-end">
                <ThemedButton
                  type="submit"
                  disabled={saving}
                  loading={saving}
                  icon={Key}
                >
                  {t('changePassword')}
                </ThemedButton>
              </div>
            </ThemedCard.Footer>
          </form>
        </ThemedCard>
      )}

      {/* Security Questions Tab */}
      {activeTab === 'questions' && (
        <ThemedCard>
          <ThemedCard.Header>
            <Shield className="inline w-5 h-5 mr-2" />
            {t('securityQuestions')}
          </ThemedCard.Header>
          <form onSubmit={handleSecurityQuestionsSubmit}>
            <ThemedCard.Body className="space-y-6">
              <p className={cn("text-sm", text.muted)}>{t('securityQuestionsHelp')}</p>
              
              {hasSecurityQuestions && (
                <div className={cn(
                  "p-3 rounded-md border",
                  "bg-yellow-50 dark:bg-yellow-900/20",
                  "border-yellow-200 dark:border-yellow-800"
                )}>
                  <p className={cn("text-sm", "text-yellow-800 dark:text-yellow-200")}>
                    <AlertCircle className="inline w-4 h-4 mr-1" />
                    {t('securityQuestionsAlreadySet')}
                  </p>
                </div>
              )}

              <div className="space-y-6">
                {/* Question 1 */}
                <div>
                  <label className={cn("block text-sm font-medium mb-2", text.secondary)}>
                    {t('securityQuestion')} 1 *
                  </label>
                  <select
                    value={securityQuestions.question_1}
                    onChange={(e) => handleSecurityQuestionChange('question_1', e.target.value)}
                    className={cn(
                      "w-full px-3 py-2 border rounded-md transition-all duration-200 mb-2",
                      "border-gray-300 dark:border-gray-600",
                      "bg-white dark:bg-gray-700",
                      text.primary,
                      "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                      "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600"
                    )}
                    required
                  >
                    <option value="">{t('selectQuestion')}</option>
                    {questionOptions.map((question, index) => (
                      <option key={index} value={question}>{question}</option>
                    ))}
                  </select>
                  <ThemedInput
                    type="text"
                    value={securityQuestions.answer_1}
                    onChange={(e) => handleSecurityQuestionChange('answer_1', e.target.value)}
                    placeholder={t('yourAnswer')}
                    required
                  />
                </div>

                {/* Question 2 */}
                <div>
                  <label className={cn("block text-sm font-medium mb-2", text.secondary)}>
                    {t('securityQuestion')} 2 *
                  </label>
                  <select
                    value={securityQuestions.question_2}
                    onChange={(e) => handleSecurityQuestionChange('question_2', e.target.value)}
                    className={cn(
                      "w-full px-3 py-2 border rounded-md transition-all duration-200 mb-2",
                      "border-gray-300 dark:border-gray-600",
                      "bg-white dark:bg-gray-700",
                      text.primary,
                      "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                      "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600"
                    )}
                    required
                  >
                    <option value="">{t('selectQuestion')}</option>
                    {questionOptions.map((question, index) => (
                      <option 
                        key={index} 
                        value={question}
                        disabled={question === securityQuestions.question_1}
                      >
                        {question}
                      </option>
                    ))}
                  </select>
                  <ThemedInput
                    type="text"
                    value={securityQuestions.answer_2}
                    onChange={(e) => handleSecurityQuestionChange('answer_2', e.target.value)}
                    placeholder={t('yourAnswer')}
                    required
                  />
                </div>

                {/* Question 3 (Optional) */}
                <div>
                  <label className={cn("block text-sm font-medium mb-2", text.secondary)}>
                    {t('securityQuestion')} 3 ({t('optional')})
                  </label>
                  <select
                    value={securityQuestions.question_3}
                    onChange={(e) => handleSecurityQuestionChange('question_3', e.target.value)}
                    className={cn(
                      "w-full px-3 py-2 border rounded-md transition-all duration-200 mb-2",
                      "border-gray-300 dark:border-gray-600",
                      "bg-white dark:bg-gray-700",
                      text.primary,
                      "focus:ring-blue-500 focus:border-blue-500 focus:ring-2",
                      "hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-600"
                    )}
                  >
                    <option value="">{t('selectQuestion')}</option>
                    {questionOptions.map((question, index) => (
                      <option 
                        key={index} 
                        value={question}
                        disabled={question === securityQuestions.question_1 || question === securityQuestions.question_2}
                      >
                        {question}
                      </option>
                    ))}
                  </select>
                  {securityQuestions.question_3 && (
                    <ThemedInput
                      type="text"
                      value={securityQuestions.answer_3}
                      onChange={(e) => handleSecurityQuestionChange('answer_3', e.target.value)}
                      placeholder={t('yourAnswer')}
                      required
                    />
                  )}
                </div>
              </div>
            </ThemedCard.Body>
            <ThemedCard.Footer>
              <div className="flex justify-end">
                <ThemedButton
                  type="submit"
                  disabled={saving}
                  loading={saving}
                  icon={Save}
                >
                  {t('saveSecurityQuestions')}
                </ThemedButton>
              </div>
            </ThemedCard.Footer>
          </form>
        </ThemedCard>
      )}
    </div>
  );
};

export default SecuritySettings;
